import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import {
  LogIn, PlayCircle, ListChecks, Keyboard, Eye, Send, BarChart3,
  Lock, ShieldCheck, ChevronLeft, ChevronRight, CheckCircle2, Video
} from "lucide-react";

const STEPS_EN = [
  { key: "login", icon: LogIn, title: "Sign in to your workspace",
    body: "Use the email and temporary password sent to you. Your assigned page range and dates unlock automatically after login." },
  { key: "training", icon: PlayCircle, title: "Read this short training",
    body: "Walk through each step here before opening the typing module. The workflow is sequential — you cannot skip ahead." },
  { key: "open", icon: ListChecks, title: "Open your current page",
    body: "From the sidebar, go to Typing. Only the next available page from your assignment is shown. Earlier completed pages remain read-only." },
  { key: "type", icon: Keyboard, title: "Type exactly what you see",
    body: "Every page has nine fields. Match each character — uppercase, lowercase, digits, symbols and spaces — exactly as shown beside each typing box." },
  { key: "review", icon: Eye, title: "Review before submitting",
    body: "Compare your input with the reference. Once submitted, the page is locked permanently and cannot be re-opened or re-submitted." },
  { key: "submit", icon: Send, title: "Submit the page",
    body: "Press Submit. Your next page in the assignment unlocks automatically — keep up the rhythm and you'll be done in no time." },
  { key: "results", icon: BarChart3, title: "Track your results",
    body: "Open Results anytime to view WPM, time taken, mistakes and per-character analytics for every page you've submitted." },
];

const STEPS_HI = [
  { key: "login", icon: LogIn, title: "अपने वर्कस्पेस में साइन इन करें",
    body: "आपको भेजी गई ईमेल और अस्थायी पासवर्ड का उपयोग करें। लॉगिन के बाद आपकी असाइन की गई पेज रेंज और तिथियाँ अपने आप अनलॉक हो जाएँगी।" },
  { key: "training", icon: PlayCircle, title: "यह छोटा प्रशिक्षण पढ़ें",
    body: "Typing मॉड्यूल खोलने से पहले हर चरण ध्यान से देखें। यह वर्कफ़्लो क्रमिक है — आप आगे नहीं जा सकते।" },
  { key: "open", icon: ListChecks, title: "अपना वर्तमान पेज खोलें",
    body: "साइडबार से Typing पर जाएँ। केवल आपका अगला उपलब्ध पेज दिखेगा। पहले से पूरे किए गए पेज केवल-पढ़ने योग्य रहेंगे।" },
  { key: "type", icon: Keyboard, title: "जो दिखे वही टाइप करें",
    body: "हर पेज में नौ फ़ील्ड्स हैं। हर अक्षर — अपरकेस, लोअरकेस, अंक, चिन्ह और स्पेस — संदर्भ के अनुसार ठीक वैसे ही टाइप करें।" },
  { key: "review", icon: Eye, title: "सबमिट से पहले समीक्षा करें",
    body: "अपने इनपुट की तुलना संदर्भ से करें। सबमिट करते ही पेज स्थायी रूप से लॉक हो जाता है।" },
  { key: "submit", icon: Send, title: "पेज सबमिट करें",
    body: "Submit दबाएँ। आपका अगला पेज अपने आप अनलॉक हो जाएगा।" },
  { key: "results", icon: BarChart3, title: "अपने परिणाम देखें",
    body: "Results पर जाकर WPM, लगा समय, गलतियाँ और हर पेज की कैरेक्टर-लेवल रिपोर्ट देखें।" },
];

export default function CandidateTraining() {
  const { lang, t } = useI18n();
  const steps = useMemo(() => (lang === "hi" ? STEPS_HI : STEPS_EN), [lang]);
  const [active, setActive] = useState(0);
  const [videoLang, setVideoLang] = useState("hi");

  const total = steps.length;
  const cur = steps[active];
  const Icon = cur.icon;
  const percent = Math.round(((active + 1) / total) * 100);

  const go = (i) => setActive(Math.max(0, Math.min(total - 1, i)));

  const videoSrc = videoLang === "hi"
    ? "/tutorials/DataSprint_Tutorial_Hindi.mp4"
    : "/tutorials/DataSprint_Tutorial_English.mp4";

  // ---- Watch-gate tracking ----
  const videoRef = useRef(null);
  const [watchPct, setWatchPct] = useState(0);        // 0.0 - 1.0
  const [threshold, setThreshold] = useState(0.9);
  const maxSeenRef = useRef(0);                        // max fraction ever reached in current session
  const lastPushedRef = useRef(0);                     // last percent value posted to server

  // Pull existing progress on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/candidate/tutorial-progress");
        setWatchPct(r.data.percent || 0);
        setThreshold(r.data.threshold || 0.9);
        maxSeenRef.current = r.data.percent || 0;
        lastPushedRef.current = r.data.percent || 0;
      } catch (e) { /* silent */ }
    })();
  }, []);

  // Persist progress (throttled — only when the max grows by ≥ 3% or hits 100%)
  const pushProgress = async (pct, isFinal = false) => {
    const dur = videoRef.current?.duration || 0;
    try {
      const r = await api.post("/candidate/tutorial-progress", {
        percent: pct,
        language: videoLang,
        duration_seconds: dur || undefined,
      });
      setWatchPct(r.data.percent || pct);
      lastPushedRef.current = r.data.percent || pct;
      if (isFinal) setThreshold(r.data.threshold || threshold);
    } catch (e) { /* silent */ }
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration || Number.isNaN(v.duration)) return;
    const frac = Math.max(0, Math.min(1, v.currentTime / v.duration));
    if (frac > maxSeenRef.current) maxSeenRef.current = frac;
    // Throttle: push whenever the max has grown by ≥ 3 percentage points
    if (maxSeenRef.current - lastPushedRef.current >= 0.03) {
      pushProgress(maxSeenRef.current);
    }
  };
  const onEnded = () => {
    maxSeenRef.current = 1;
    pushProgress(1, true);
  };

  // Watch-gate display kept off; progress still tracked via API for analytics.

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{lang === "hi" ? "ऑनबोर्डिंग" : "Onboarding"}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("training_videos")}</h1>
          <p className="text-zinc-500 mt-2 max-w-2xl text-sm">
            {lang === "hi"
              ? "DataSprint टाइपिंग प्रक्रिया का चरण-दर-चरण मार्गदर्शक। Typing मॉड्यूल खोलने से पहले इसे पूरा करें।"
              : "A step-by-step walkthrough of the DataSprint typing workflow. Complete this before opening Typing."}
          </p>
        </div>
        <span className="chip"><ShieldCheck size={12} className="text-[var(--accent)]" /> Required</span>
      </div>

      {/* Progress bar */}
      <div className="mt-6 rounded-2xl bg-white border border-[var(--line)] p-5 shadow-[0_1px_2px_rgba(10,10,10,0.04)]">
        <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
          <span className="font-mono">Step {active + 1} / {total}</span>
          <span className="font-mono">{percent}%</span>
        </div>
        <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[var(--ink)] to-[var(--accent)] transition-[width] duration-500 ease-out"
            style={{ width: `${percent}%` }} />
        </div>
      </div>

      {/* Tutorial Video (voiceover Hindi + English) */}
      <div className="mt-6 rounded-2xl bg-white border border-[var(--line)] overflow-hidden shadow-[0_1px_2px_rgba(10,10,10,0.04)]" data-testid="tutorial-video-card">
        <div className="flex items-center justify-between flex-wrap gap-3 px-5 py-4 border-b border-[var(--line)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center">
              <Video size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                {lang === "hi" ? "पूरा वॉकथ्रू वीडियो" : "Full Walkthrough Video"}
              </div>
              <div className="font-display font-semibold text-[15px]">
                {lang === "hi"
                  ? "आवाज़ के साथ चरण-दर-चरण मार्गदर्शन"
                  : "Step-by-step voiceover walkthrough"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-[var(--line)] p-0.5 bg-zinc-50">
              <button
                data-testid="tutorial-lang-hi"
                onClick={() => setVideoLang("hi")}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  videoLang === "hi" ? "bg-[var(--ink)] text-white" : "text-zinc-600 hover:text-zinc-900"
                }`}
              >हिंदी</button>
              <button
                data-testid="tutorial-lang-en"
                onClick={() => setVideoLang("en")}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  videoLang === "en" ? "bg-[var(--ink)] text-white" : "text-zinc-600 hover:text-zinc-900"
                }`}
              >English</button>
            </div>
          </div>
        </div>
        <div className="bg-black">
          <video
            key={videoSrc}
            data-testid="tutorial-video-player"
            ref={videoRef}
            src={videoSrc}
            controls
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            onTimeUpdate={onTimeUpdate}
            onEnded={onEnded}
            playsInline
            preload="metadata"
            className="w-full h-auto max-h-[520px] block mx-auto"
          />
        </div>
      </div>

      {/* Watch-gate reverted per user request — direct Start Typing is available on Home. */}

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* Stepper list */}
        <ol className="lg:col-span-1 space-y-2.5">
          {steps.map((s, i) => {
            const SIcon = s.icon;
            const done = i < active;
            const curStep = i === active;
            return (
              <li key={s.key}>
                <button data-testid={`train-step-${i + 1}`} onClick={() => go(i)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                    curStep
                      ? "border-[var(--ink)] bg-[var(--ink)] text-white shadow-[0_8px_24px_-12px_rgba(10,10,10,0.5)]"
                      : done
                      ? "border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50"
                      : "border-[var(--line)] bg-white hover:border-zinc-400 hover:bg-zinc-50"
                  }`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                    curStep ? "bg-white text-[var(--ink)]"
                    : done ? "bg-emerald-500 text-white"
                    : "bg-zinc-100 text-zinc-600"
                  }`}>
                    {done ? <CheckCircle2 size={16} /> : <SIcon size={16} />}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-[10px] uppercase tracking-[0.18em] ${curStep ? "text-zinc-300" : "text-zinc-400"}`}>
                      {i + 1} / {total}
                    </div>
                    <div className={`font-display font-semibold text-sm sm:text-[15px] truncate mt-0.5 ${curStep ? "" : "text-zinc-800"}`}>
                      {s.title}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>

        {/* Main panel with key-based remount for smooth fade animation */}
        <div className="lg:col-span-2">
          <div
            key={cur.key}
            className="card-clean p-6 sm:p-8 lg:p-10 relative overflow-hidden animate-[fadeup_360ms_ease-out]"
          >
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[var(--accent)]/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[var(--ink)] text-white flex items-center justify-center shadow-sm">
                  <Icon size={22} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Step {active + 1}</div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-0.5">{cur.title}</h2>
                </div>
              </div>

              <p className="mt-6 text-zinc-700 leading-relaxed text-[15px]">{cur.body}</p>

              {cur.key === "type" && (
                <div className="mt-6 rounded-xl bg-zinc-900 text-white font-mono text-xs sm:text-sm leading-relaxed overflow-x-auto p-5 border border-zinc-800">
                  <div className="opacity-60 text-[10px] uppercase tracking-[0.18em] mb-2">Example reference field</div>
                  <div className="whitespace-pre-wrap break-all">
                    P.XkhA zVCPcl_mkY$zeY&gt;eQIK]No.UKtNt VL:XEeW ;MAX{"\n"}
                    nN[joxzd gL:zrlbG QNn%mcfj#kmfsrMU%ryjjflt S#ny+
                  </div>
                </div>
              )}

              {cur.key === "review" && (
                <div className="mt-6 flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-4">
                  <Lock size={16} className="shrink-0" />
                  <span>{lang === "hi"
                    ? "एक बार सबमिट करने के बाद पेज स्थायी रूप से लॉक हो जाता है।"
                    : "After submission, the page is permanently locked. Review carefully."}</span>
                </div>
              )}

              {/* Nav controls */}
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-[var(--line)]">
                <button
                  data-testid="train-prev"
                  disabled={active === 0}
                  onClick={() => go(active - 1)}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[var(--line)] text-sm font-semibold hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} /> {lang === "hi" ? "पिछला" : "Previous"}
                </button>

                {/* Dots */}
                <div className="flex gap-1.5">
                  {steps.map((s, i) => (
                    <button
                      key={s.key}
                      onClick={() => go(i)}
                      aria-label={`Go to step ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === active ? "w-8 bg-[var(--ink)]"
                        : i < active ? "w-3 bg-emerald-500"
                        : "w-3 bg-zinc-300 hover:bg-zinc-400"
                      }`}
                    />
                  ))}
                </div>

                {active < total - 1 ? (
                  <button
                    data-testid="train-next"
                    onClick={() => go(active + 1)}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[var(--ink)] text-white text-sm font-semibold hover:bg-[var(--accent)] transition-colors"
                  >
                    {lang === "hi" ? "अगला" : "Next"} <ChevronRight size={16} />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-semibold">
                    <CheckCircle2 size={16} /> {lang === "hi" ? "पूरा" : "Complete"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inline keyframes for the fade-up animation (scoped via animate-[fadeup_...]) */}
      <style>{`
        @keyframes fadeup {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
